import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { resolveInstallClosure } from '@omni/kernel/dep-resolver'
import { kernel } from '@omni/kernel'
import { navigate } from '../shell/router'
import { ArrowLeft, Puzzle } from '@omni/icon-kit'
import { GROUPS, ENGINE_GROUP, OTHER_GROUP, groupOf, isEngine } from './plugin-groups'
import { turnOnWithNeeds } from './needs'

function useKernelState() {
  return useSyncExternalStore(
    useCallback((cb) => {
      const d = kernel.subscribe(cb)
      return () => d.dispose()
    }, []),
    () => kernel.getState()
  )
}

const STATUS_BADGE = {
  active: 'badge-success',
  loading: 'badge-info',
  blocked: 'badge-warning',
  error: 'badge-error',
  disabled: 'badge-ghost',
  inactive: 'badge-ghost',
}

const trace = (step, data) => window.__omni?.services?.getNow?.('omni.dev-logs')?.channel?.('plugins')?.trace?.(step, data)

function PluginRow({ manifest, installedEntry, onNote }) {
  const state = useKernelState()
  const installed = Boolean(installedEntry)
  const enabled = installedEntry?.enabled ?? false
  const rt = state.runtime.get(manifest.id)
  const status = installed ? (enabled ? (rt?.status ?? 'loading') : 'disabled') : 'inactive'
  const deps = Object.keys(manifest.dependencies ?? {})
  const registryById = new Map(state.registry.map((m) => [m.id, m]))

  const toggleEnabled = async (next) => {
    if (!next) {
      const dependents = kernel.dependentsOf(manifest.id)
      if (dependents.length) {
        const names = dependents.map((d) => {
          const m = registryById.get(d)
          return m?.name ?? d
        })
        const ok = confirm(`Switching off ${manifest.name} also switches off: ${names.join(', ')}. Go on?`)
        if (!ok) return
        trace('turn-off', { id: manifest.id, also: names })
        await kernel.setEnabled(manifest.id, false, { cascade: dependents })
        onNote(`${manifest.name} is off. Also switched off: ${names.join(', ')}.`)
        return
      }
      trace('turn-off', { id: manifest.id, also: [] })
      await kernel.setEnabled(manifest.id, false)
      onNote(`${manifest.name} is off.`)
      return
    }
    // Turning ON
    const also = await turnOnWithNeeds(kernel, manifest.id)
    trace('turn-on', { id: manifest.id, also })
    onNote(also.length ? `${manifest.name} is on. Also switched on, because it needs them: ${also.join(', ')}.` : `${manifest.name} is on.`)
  }

  const uninstall = async () => {
    const dependents = kernel.dependentsOf(manifest.id)
    const names = dependents.map((d) => {
      const m = registryById.get(d)
      return m?.name ?? d
    })
    const ok = confirm(
      dependents.length
        ? `Uninstall "${manifest.name}"? These depend on it and will be disabled:\n${names.join(', ')}`
        : `Uninstall "${manifest.name}"?`
    )
    if (!ok) return
    for (const d of dependents) {
      kernel.store.setEnabled(d, false, `cascade:${manifest.id}`)
    }
    await kernel.uninstall(manifest.id)
  }

  return (
    <li data-test="plugin-card" data-id={manifest.id} className="flex items-start gap-3 p-3">
      <span className="text-xl w-8 text-center">{manifest.icon ?? <Puzzle size={24} />}</span>
      <div className="min-w-0 flex-1">
        <div className="font-medium">{manifest.name}</div>
        {(manifest.summary || manifest.description) && (
          <p data-test="plugin-summary" className="text-sm text-base-content/70">
            {manifest.summary || manifest.description}
          </p>
        )}
        {rt?.reason && (
          <p className="text-error text-xs" title={rt.reason}>
            {rt.reason}
          </p>
        )}
        {(manifest.description || deps.length > 0) && (
          <details data-test="plugin-details" className="text-xs text-base-content/60">
            <summary className="cursor-pointer">More</summary>
            <div className="mt-1 flex flex-col gap-1">
              {manifest.summary && manifest.description && <span>{manifest.description}</span>}
              {deps.length > 0 && (
                <span>
                  Needs:{' '}
                  {deps
                    .map((dep) => {
                      const m = registryById.get(dep)
                      return m?.name ?? dep
                    })
                    .join(', ')}
                </span>
              )}
              {installed && !manifest.builtin && !isEngine(manifest.id) && (
                <button className="btn btn-ghost btn-xs mt-1" onClick={uninstall}>
                  Uninstall
                </button>
              )}
            </div>
          </details>
        )}
      </div>
      <div className="flex flex-col items-end gap-2">
        {(status === 'blocked' || status === 'error') && <span className={`badge badge-sm ${STATUS_BADGE[status]}`}>{status}</span>}
        {isEngine(manifest.id) && enabled && <span className="text-xs text-base-content/60">Always on</span>}
        {!isEngine(manifest.id) && (
          <input
            type="checkbox"
            data-test="plugin-toggle"
            className="toggle toggle-primary toggle-sm"
            checked={installed && enabled}
            aria-label={`Switch ${manifest.name} on or off`}
            onChange={(e) => toggleEnabled(e.target.checked)}
          />
        )}
      </div>
    </li>
  )
}

// paste the URL of a plugin.json (with an absolute entry.url) to install a
// plugin that is not in the registry
function AddByUrl() {
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const add = async () => {
    if (!url) return
    setBusy(true)
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`fetch failed: ${res.status}`)
      const manifest = await res.json()
      if (!manifest.id || !manifest.name || !manifest.version) {
        throw new Error('manifest needs id, name and version')
      }
      if (manifest.kind !== 'pack' && !manifest.entry?.url) {
        throw new Error('manifest.entry.url must be an absolute bundle URL')
      }
      const registryById = new Map(
        kernel.getState().registry.map((m) => [m.id, m])
      )
      registryById.set(manifest.id, manifest)
      const installedIds = new Set(Object.keys(kernel.getState().installed))
      const { toInstall, errors } = resolveInstallClosure(
        manifest.id,
        registryById,
        installedIds
      )
      if (errors.length) throw new Error(errors.join('\n'))
      const extras = toInstall.filter((m) => m.id !== manifest.id)
      if (
        extras.length &&
        !confirm(
          `"${manifest.name}" also needs:\n` +
            extras.map((m) => `• ${m.name} ${m.version}`).join('\n')
        )
      ) {
        return
      }
      await kernel.installMany(toInstall)
      setUrl('')
    } catch (err) {
      alert(`Could not add plugin:\n${err.message ?? err}`)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="join w-full">
      <input
        className="input input-sm join-item w-full"
        placeholder="Add plugin by URL (link to its plugin.json)"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && add()}
      />
      <button className="btn btn-sm join-item" disabled={busy} onClick={add}>
        {busy ? '…' : 'Add'}
      </button>
    </div>
  )
}

export function ListingPage() {
  const state = useKernelState()
  const [query, setQuery] = useState('')
  const [note, setNote] = useState('')
  const registryIds = new Set(state.registry.map((m) => m.id))
  // installed plugins that are no longer in the registry still get a card
  const orphans = Object.values(state.installed)
    .map((e) => e.manifest)
    .filter((m) => !registryIds.has(m.id))

  const allManifests = [...state.registry, ...orphans]
  const registryById = new Map(allManifests.map((m) => [m.id, m]))

  const matchesQuery = (manifest) => {
    if (!query) return true
    const q = query.toLowerCase()
    return (
      manifest.name.toLowerCase().includes(q) ||
      (manifest.summary && manifest.summary.toLowerCase().includes(q)) ||
      (manifest.description && manifest.description.toLowerCase().includes(q))
    )
  }

  useEffect(() => {
    if (note) {
      const timer = setTimeout(() => setNote(''), 8000)
      return () => clearTimeout(timer)
    }
  }, [note])

  const engineMatches = ENGINE_GROUP.ids.filter((id) => registryById.has(id) && matchesQuery(registryById.get(id))).length > 0

  return (
    <div className="bg-base-200 min-h-full">
      <div className="navbar bg-base-100 shadow">
        <button className="btn btn-ghost" onClick={() => navigate('/')}>
          <ArrowLeft size={16} /> Board
        </button>
        <span className="flex-1 text-lg font-semibold">Plugins</span>
        <input
          type="text"
          placeholder="Search plugins"
          data-test="plugin-search"
          className="input input-sm w-56"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="mx-auto flex max-w-4xl flex-col gap-6 p-4">
        {state.registryError && <div className="alert alert-error">Could not load the plugin registry: {state.registryError}</div>}

        {note && (
          <div data-test="plugin-note" role="status" className="alert alert-info py-2 text-sm">
            {note}
          </div>
        )}

        {[...GROUPS, OTHER_GROUP].map((group) => {
          const groupManifests = allManifests.filter((m) => groupOf(m.id).id === group.id && matchesQuery(m))
          if (groupManifests.length === 0) return null
          return (
            <section key={group.id} data-test="plugin-group" className="flex flex-col gap-2">
              <div>
                <h2 className="font-semibold">{group.name}</h2>
                <div className="flex items-center gap-2">
                  {group.level && <span className="badge badge-ghost badge-sm">Bloom: {group.level}</span>}
                  <p className="text-sm text-base-content/60">{group.blurb}</p>
                </div>
              </div>
              <ul className="bg-base-100 rounded-box divide-base-200 divide-y shadow-sm">
                {groupManifests.map((manifest) => (
                  <PluginRow key={manifest.id} manifest={manifest} installedEntry={state.installed[manifest.id]} onNote={setNote} />
                ))}
              </ul>
            </section>
          )
        })}

        <details data-test="engine-parts" className="bg-base-100 rounded-box p-3 shadow-sm" open={query !== '' && engineMatches}>
          <summary className="cursor-pointer font-semibold">
            Engine parts ({ENGINE_GROUP.ids.length}){' '}
            <span className="text-sm font-normal text-base-content/60">always on, everything else is built on them</span>
          </summary>
          <ul className="bg-base-100 rounded-box divide-base-200 divide-y shadow-sm mt-3">
            {ENGINE_GROUP.ids.map((id) => {
              if (!registryById.has(id) || !matchesQuery(registryById.get(id))) return null
              const manifest = registryById.get(id)
              return <PluginRow key={manifest.id} manifest={manifest} installedEntry={state.installed[manifest.id]} onNote={setNote} />
            })}
          </ul>
        </details>

        <details className="text-sm">
          <summary className="cursor-pointer text-base-content/60">Add a plugin from a link</summary>
          <div className="mt-3">
            <AddByUrl />
          </div>
        </details>
      </div>
    </div>
  )
}
